import { format } from "date-fns";
import { isEmpty } from "lodash";
import {
  IExcludedDay,
  IExcludedWeekDay,
} from "src/Pages/Company/Absences/Calendars/Calendars.models";

/**
 * Function getting all dates for a given weekday in a particular year.
 * @param {number} year - year to get dates from.
 * @param {number} dayOfWeek - week day (0-6, 0 for Sunday) to get dates for.
 * @returns {array} collection of all dates for given week day in a year.
 */
export const getAllWeekdaysInYear = (year: number, dayOfWeek: number) => {
  const dates = [];

  // Start from the 1st of January of the given year
  const date = new Date(year, 0, 1);

  // Find the first occurrence of the desired day of the week in the year
  while (date.getDay() !== dayOfWeek) {
    date.setDate(date.getDate() + 1);
  }

  // Add every following occurrence of that day in the year
  while (date.getFullYear() === year) {
    dates.push({ date: format(date, "yyyy-MM-dd") }); // Add a copy of the date to the array
    date.setDate(date.getDate() + 7); // Move to the next week
  }

  return dates;
};

/**
 * Function checking if a week day is globally disabled in whole year.
 * @param days - collection of excluded days from API.
 * @param year - calendar year.
 * @param targetWeekday - week day (0-6, 0 for Sunday).
 * @returns {boolean}
 */
export const checkExcludedWeekDays = (
  days: IExcludedDay[],
  year: string,
  targetWeekday: number
) => {
  if (isEmpty(days)) return false;

  // Get all occurrences of the target weekday in the given year
  const allWeekdays = getAllWeekdaysInYear(Number(year), targetWeekday);

  // Extract the dates from the input array and store them as strings
  const providedDates = days.map((day) => day.date);

  // Check if every weekday occurrence in the year is included in the provided dates
  return allWeekdays.every((weekday) => providedDates.includes(weekday.date));
};

/**
 * Function determining which week days in a year are disabled globally.
 * @param days - collection of excluded days from API.
 * @param year - calendar year.
 * @returns {array} - collection of disabled week days.
 */
export const getExcludedWeekDays = (days: IExcludedDay[], year: string) => {
  const excludedWeekDays: IExcludedWeekDay[] = [];

  weekDayOptions.forEach((weekDay) => {
    const isExcluded = checkExcludedWeekDays(days, year, weekDay.id);

    if (isExcluded) {
      excludedWeekDays.push(weekDay);
    }
  });

  return excludedWeekDays;
};

/**
 * Function setting options in year select.
 * @param year - current year (for new calendars) or calendar year (for edited calendars).
 * @param offsetFuture - number of future years.
 * @param offsetPast - optional number of past years.
 * @returns {array} - collection of options.
 */
export const getYearRange = (
  year: string,
  offsetFuture: number,
  offsetPast?: number
) => {
  const currentYear = Number(year);
  const years = [];

  // Add past years if offsetPast is provided
  if (offsetPast) {
    // eslint-disable-next-line no-plusplus
    for (let i = offsetPast; i > 0; i--) {
      years.push((currentYear - i).toString());
    }
  } else {
    // Push just the year before if offsetPast is not provided
    years.push((currentYear - 1).toString());
  }

  // Push the given year
  years.push(currentYear.toString());

  // Push the future years
  // eslint-disable-next-line no-plusplus
  for (let i = 1; i <= offsetFuture; i++) {
    years.push((currentYear + i).toString());
  }

  return years;
};

export const weekDayOptions = [
  {
    label: "Poniedziałek",
    id: 1,
  },
  {
    label: "Wtorek",
    id: 2,
  },
  {
    label: "Środa",
    id: 3,
  },
  {
    label: "Czwartek",
    id: 4,
  },
  {
    label: "Piątek",
    id: 5,
  },
  {
    label: "Sobota",
    id: 6,
  },
  {
    label: "Niedziela",
    id: 0,
  },
];
